<template>
  <div class="chapter-page">
    <!-- Sidebar for Wide Screens -->
    <aside class="chapter-sidebar" v-if="isDesktop">
      <div class="book-cover-container">
        <img :src="chapter?.book_cover || defaultCover" alt="Book Cover" class="sidebar-cover" />
        <!-- Title overlay -->
        <div class="book-title-overlay">
          <h3>{{ chapter?.book_title || "Loading..." }}</h3>
        </div>
      </div>
      <hr class="chapter-divider" />
      <ul class="chapter-list">
        <li v-for="ch in chapters" :key="ch.slug"
            :class="['chapter-item', { 'active-chapter': ch.slug === chapter?.slug }]">

          <router-link
            v-if="canAccessChapter(ch)"
            :to="`/books/${bookSlug}/chapters/${ch.slug}`">
            {{ ch.title }}
          </router-link>

          <span v-else @click="showLoginModal" class="restricted-chapter">
            {{ ch.title }} <span class="text-warning"><font-awesome-icon :icon="['fas', 'lock']" /></span>
          </span>
        </li>
      </ul>
    </aside>

    <!-- Mobile Dropdown Navigation -->
    <div class="mobile-nav" v-else>
      <button class="menu-btn" @click="showDropdown = !showDropdown">☰ Chapters</button>
      <ul v-if="showDropdown" class="dropdown-list">
        <li v-for="ch in chapters" :key="ch.slug">
          <router-link
            v-if="canAccessChapter(ch)"
            :to="`/books/${bookSlug}/chapters/${ch.slug}`">
            {{ ch.title }}
          </router-link>

          <span v-else @click="showLoginModal" class="restricted-chapter">
            {{ ch.title }} <span class="text-warning"><font-awesome-icon :icon="['fas', 'lock']" /></span>
          </span>
        </li>
      </ul>
    </div>

    <!-- Session alert modal -->
    <div v-if="showSessionAlert" class="modal-overlay">
      <div class="modal-content">
        <h3><font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="warning-icon" /> Sesi Membaca Aktif</h3>
        <p>{{ sessionAlertMessage }}</p>
        <div class="modal-actions">
          <button @click="continueWithCurrentDevice" class="btn btn-primary">Lanjutkan di Perangkat Ini</button>
          <button @click="closeSessionAlert" class="btn btn-secondary">Tutup</button>
        </div>
      </div>
    </div>

    <!-- Main Chapter Content -->
    <div class="chapter-container">
      <router-link :to="`/book/${bookSlug}`" class="back-link">⬅ Kembali ke Buku</router-link>
      <h3 class="chapter-title">{{ chapter?.title || "Loading..." }}</h3>
      <p class="chapter-meta text-secondary fs-6">
        Diperbarui: {{ formatDate(chapter?.updated_date) }} |
        Jumlah Kata: {{ chapter?.word_count }} |
        Waktu Baca: {{ chapter?.estimated_read_time }} menit
      </p>
      <hr class="chapter-divider" />
      <div class="chapter-content" ref="chapterContent">
        <div v-html="chapter?.content"></div>
        <div class="copy-protection-notice">
          Hak cipta dilindungi. Konten ini tidak boleh disalin atau didistribusikan tanpa izin.
        </div>
      </div>

      <!-- Comment Section -->
      <div class="comment-section">
        <h4>Komentar</h4>
        <hr class="chapter-divider" />

        <!-- Comment Form for Logged-in Users -->
        <div v-if="isLoggedIn" class="comment-form">
          <textarea v-model="newComment" placeholder="Tulis komentar Anda..." rows="3"></textarea>
          <button @click="submitComment" class="btn btn-primary">Kirim Komentar</button>
        </div>
        <!-- Message for Non-logged-in Users -->
        <div v-else class="login-to-comment">
          <p>Silakan <span @click="showLoginModal" class="login-link">login</span> untuk mengirim komentar.</p>
        </div>

        <!-- List of Comments -->
        <div v-if="comments.length > 0" class="comments-list">
          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <div class="comment-header">
              <img v-if="comment.avatar" :src="comment.avatar" alt="User Avatar" class="avatar" />
              <font-awesome-icon v-else :icon="['fas', 'user']" class="default-avatar" />
              <span class="comment-user">{{ comment.user }}</span>
              <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
            </div>
            <p class="comment-content">{{ comment.content }}</p>
          </div>
        </div>
        <div v-else class="no-comments">
          <p>Belum ada komentar. Jadilah yang pertama mengomentari!</p>
        </div>
      </div>
    </div>

    <!-- Login Modal -->
    <div v-if="isLoginModalVisible" class="modal-overlay">
      <div class="modal-content">
        <h3>Akses Terbatas</h3>
        <p>Silakan login atau daftar untuk membaca bab ini.</p>
        <LoginForm @login-success="handleLoginSuccess" />
        <button @click="redirectToRegister" class="btn btn-secondary">Daftar</button>
        <button @click="isLoginModalVisible = false" class="btn btn-close">Tutup</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import LoginForm from "@/components/LoginForm.vue";
import Swal from 'sweetalert2';
import { useHead } from '@unhead/vue';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: "Chapter",
  components: { LoginForm },
  setup() {
    const chapter = ref({
      book_cover: "",
      book_title: "",
      title: "Loading...",
      content: "Loading...",
      word_count: 0,
      estimated_read_time: 0,
    });
    const chapterContent = ref(null);
    const lastToastTime = ref(0);
    const TOAST_COOLDOWN = 2000; // 2 seconds cooldown

    // Create computed properties for meta information
    const pageTitle = computed(() => {
      return chapter.value?.title ? `${chapter.value.title} - ${chapter.value.book_title} | HobiBaca` : "Membaca Buku | HobiBaca";
    });

    const pageDescription = computed(() => {
      if (!chapter.value?.content) return "Baca bab-bab menarik di HobiBaca.";

      // Strip HTML and get first 150 characters
      const contentText = stripHtmlTags(chapter.value.content);
      return contentText.substring(0, 150) + (contentText.length > 150 ? '...' : '');
    });

    const stripHtmlTags = (html) => {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent || "";
    };

    // Use the useHead composable to set meta tags
    useHead({
      title: pageTitle,
      meta: [
        { name: 'description', content: pageDescription },
        { name: 'keywords', content: computed(() => {
          if (!chapter.value?.title) return "HobiBaca, baca, buku, bab";
          return `${chapter.value.title}, ${chapter.value.book_title}, HobiBaca, baca, buku, bab`;
        })},
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: pageDescription },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: computed(() => window.location.href) },
        { property: 'og:image', content: computed(() => chapter.value?.book_cover || '') },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: pageTitle },
        { name: 'twitter:description', content: pageDescription },
        { name: 'twitter:image', content: computed(() => chapter.value?.book_cover || '') }
      ]
    });

    // Optimized copy protection
    const showToast = (message) => {
      const now = Date.now();
      if (now - lastToastTime.value < TOAST_COOLDOWN) return;
      
      lastToastTime.value = now;
      Swal.fire({
        icon: 'info',
        title: 'Perhatian',
        text: message,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    };

    const preventCopy = (event) => {
      // Check if user is premium or admin before preventing copy
      const userRole = localStorage.getItem("role") || "guest";
      const isPremium = ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all", 
                          "curator", "finance", "superadmin"].includes(userRole);
      
      if (isPremium) {
        // Premium users can copy, but we'll still track it
        return;
      }
      
      event.preventDefault();
      
      // Allow copying very small selections (like a word or two for search)
      const selection = window.getSelection();
      if (selection && selection.toString().length <= 20) {
        return; // Allow copying small portions of text
      }
      
      showToast('Menyalin isi bab ini tidak diperkenankan.');
    };

    const preventKeyShortcuts = (event) => {
      // Check if user is premium or admin
      const userRole = localStorage.getItem("role") || "guest";
      const isPremium = ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all", 
                          "curator", "finance", "superadmin"].includes(userRole);
      
      if (isPremium) {
        return; // Premium users can use shortcuts
      }
      
      // Only prevent specific key combinations
      if ((event.ctrlKey || event.metaKey) && 
          (event.key === 'c' || event.key === 'x' || event.key === 'a' || 
           event.key === 'p' || event.key === 's')) {
        event.preventDefault();
        showToast('Penggunaan shortcut keyboard dibatasi pada halaman ini.');
      }
    };

    onMounted(() => {
      if (chapterContent.value) {
        // Use a more user-friendly approach for text selection
        // Allow selection for better reading experience, but prevent copy
        chapterContent.value.style.userSelect = 'text';
        
        // Only add event listeners to the chapter content element
        chapterContent.value.addEventListener('copy', preventCopy);
        chapterContent.value.addEventListener('cut', preventCopy);
        
        // For keyboard shortcuts, we still need document level
        document.addEventListener('keydown', preventKeyShortcuts);
        
        // Additional protection for contextmenu (right-click)
        chapterContent.value.addEventListener('contextmenu', (e) => {
          // Allow context menu for premium users
          const userRole = localStorage.getItem("role") || "guest";
          const isPremium = ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all", 
                              "curator", "finance", "superadmin"].includes(userRole);
          
          if (!isPremium) {
            e.preventDefault();
            showToast('Menu konteks dibatasi pada halaman ini.');
            return false;
          }
        });
      }
    });

    onBeforeUnmount(() => {
      if (chapterContent.value) {
        chapterContent.value.removeEventListener('copy', preventCopy);
        chapterContent.value.removeEventListener('cut', preventCopy);
        chapterContent.value.removeEventListener('contextmenu', preventCopy);
      }
      document.removeEventListener('keydown', preventKeyShortcuts);
    });

    return {
      chapter,
      chapterContent,
      preventCopy,
      stripHtmlTags
    };
  },
  data() {
    return {
      chapters: [],
      comments: [],
      newComment: "",
      isLoggedIn: !!localStorage.getItem("token"),
      bookSlug: this.$route.params.bookSlug,
      showDropdown: false,
      isDesktop: window.innerWidth > 900,
      isLoginModalVisible: false,
      userRole: localStorage.getItem("role") || "guest",
      defaultCover: "https://via.placeholder.com/150x200?text=No+Cover",
      
      // Reading Time Tracking
      readingStartTime: null,
      readingInterval: null,
      activityCheckInterval: null, // New property to track the activity check interval
      readTime: 0,
      chapterViewId: null,
      lastTimeUpdate: 0,
      
      // Idle detection
      idleTimeout: null,
      isIdle: false,
      maxIdleTime: 300000, // 5 menit dalam milidetik
      
      // Tab/window visibility
      isVisible: true,
      
      // Advanced Reading Analytics
      lastActivityTime: Date.now(),
      isUserActive: true,
      activityEvents: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'touchmove'],
      maxInactivityTime: 120000, // 2 menit dalam milidetik
      scrollPositions: [],
      lastScrollPosition: 0,
      readingActivityScore: 50, // Start with neutral score
      
      // Device detection
      isMobileDevice: false,
      touchStartTimestamp: 0,
      touchEndTimestamp: 0,
      lastTouchPosition: { x: 0, y: 0 },
      touchMovements: [],
      
      // Session Management
      sessionId: null,
      sessionActive: false,
      sessionCheckInterval: null,
      lastSessionValidation: 0,
      deviceIdentifier: null,
      showSessionAlert: false,
      sessionAlertMessage: '',
      otherDeviceInfo: '',
      pageSwitches: 0,
      pendingRequest: null,
      isDestroyed: false,
      lastToastTime: 0, // Tambahkan property ini untuk throttling toast messages
    };
  },
  async created() {
    // Generate device ID if not exists
    this.deviceIdentifier = localStorage.getItem('device_id') || this.generateDeviceId();
    localStorage.setItem('device_id', this.deviceIdentifier);
    
    // Detect mobile device
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    await this.fetchChapter();
    await this.fetchComments();
    
    // Start reading session for logged-in users
    if (this.isLoggedIn) {
      await this.startReadingSession();
    }

    // Only start reading timer after chapterViewId is set
    if ((!this.isLoggedIn || this.sessionActive) && this.chapterViewId) {
      this.startReadingTimer();
    } else if (!this.chapterViewId) {
      this.showToast('Gagal mendapatkan ID tampilan bab. Memuat ulang...');
      await this.retryFetchChapterViewId();
    }
    
    // Start session validity checks for logged-in users
    if (this.isLoggedIn && this.sessionId) {
      this.sessionCheckInterval = setInterval(this.validateSession, 60000); // Check every minute
    }
    
    // Event listener untuk page visibility API (deteksi tab aktif/tidak aktif)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Event listener untuk scroll dan interaksi pengguna
    window.addEventListener('scroll', this.resetIdleTimer);
    window.addEventListener('mousemove', this.resetIdleTimer);
    window.addEventListener('keypress', this.resetIdleTimer);
    window.addEventListener('click', this.resetIdleTimer);
    
    // Track tab/app switches
    document.addEventListener('visibilitychange', this.trackVisibilityChange);
    
    // Event listener saat pengguna meninggalkan halaman
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
    // Listen for window resize to toggle mobile/desktop view
    window.addEventListener('resize', this.handleResize);
  },
  mounted() {
    // Add passive event listeners for better performance
    const passiveOptions = { passive: true };
    
    // Add activity event listeners
    this.activityEvents.forEach(event => {
      window.addEventListener(event, this.registerActivity, passiveOptions);
    });
    
    // Add scroll progression listener
    window.addEventListener('scroll', this.trackScrollProgression, passiveOptions);
    
    // Start activity check interval
    if (!this.activityCheckInterval) {
      this.activityCheckInterval = setInterval(this.checkActivity, 30000); // Check every 30 seconds
    }
    
    // Add touch event listeners for mobile
    if (this.isMobileDevice) {
      document.addEventListener('touchstart', this.handleTouchStart, passiveOptions);
      document.addEventListener('touchend', this.handleTouchEnd, passiveOptions);
      document.addEventListener('touchmove', this.handleTouchMove, passiveOptions);
    }
    
    // Apply copy protection if chapterContent exists
    if (this.chapterContent) {
      this.chapterContent.style.userSelect = 'text';
      
      // Add copy protection listeners
      this.chapterContent.addEventListener('copy', this.preventCopy);
      this.chapterContent.addEventListener('cut', this.preventCopy);
      
      // Add keyboard shortcut protection
      document.addEventListener('keydown', this.preventKeyShortcuts);
      
      // Add context menu protection
      this.chapterContent.addEventListener('contextmenu', (e) => {
        const userRole = localStorage.getItem("role") || "guest";
        const isPremium = ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all", 
                          "curator", "finance", "superadmin"].includes(userRole);
        
        if (!isPremium) {
          e.preventDefault();
          this.showToast('Menu konteks dibatasi pada halaman ini.');
          return false;
        }
      });
    }
  },
  beforeUnmount() {
    // Flag component as destroyed
    this.isDestroyed = true;

    // Stop all timers and intervals
    this.stopReadingTimer();
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    
    // Cancel any pending requests
    if (this.pendingRequest) {
      this.pendingRequest.cancel();
      this.pendingRequest = null;
    }
    
    // Send final reading time update
    this.sendReadingTime();
    
    // Clean up chapter content listeners
    if (this.chapterContent) {
      this.chapterContent.removeEventListener('copy', this.preventCopy);
      this.chapterContent.removeEventListener('cut', this.preventCopy);
      this.chapterContent.removeEventListener('contextmenu', this.preventCopy);
    }
    
    // Remove all global event listeners
    const eventsToRemove = [
      ['document', 'visibilitychange', this.handleVisibilityChange],
      ['document', 'visibilitychange', this.trackVisibilityChange],
      ['document', 'keydown', this.preventKeyShortcuts],
      ['window', 'scroll', this.resetIdleTimer],
      ['window', 'mousemove', this.resetIdleTimer],
      ['window', 'keypress', this.resetIdleTimer],
      ['window', 'click', this.resetIdleTimer],
      ['window', 'beforeunload', this.handleBeforeUnload],
      ['window', 'resize', this.handleResize],
      ['window', 'scroll', this.trackScrollProgression]
    ];
    
    eventsToRemove.forEach(([target, event, handler]) => {
      try {
        if (target === 'document') {
          document.removeEventListener(event, handler);
        } else {
          window.removeEventListener(event, handler);
        }
      } catch (e) {
        console.error(`Error removing ${event} listener:`, e);
      }
    });
    
    // Remove activity event listeners
    this.activityEvents.forEach(event => {
      try {
        window.removeEventListener(event, this.registerActivity);
      } catch (e) {
        console.error(`Error removing ${event} listener:`, e);
      }
    });
    
    // Remove mobile touch event listeners
    if (this.isMobileDevice) {
      const touchEvents = [
        ['touchstart', this.handleTouchStart],
        ['touchend', this.handleTouchEnd],
        ['touchmove', this.handleTouchMove]
      ];
      
      touchEvents.forEach(([event, handler]) => {
        try {
          document.removeEventListener(event, handler);
        } catch (e) {
          console.error(`Error removing ${event} listener:`, e);
        }
      });
    }
  },
  watch: {
    "$route.params.chapterSlug": async function() {
      // Clean up all intervals and listeners before loading new chapter
      this.stopReadingTimer();
      if (this.activityCheckInterval) {
        clearInterval(this.activityCheckInterval);
        this.activityCheckInterval = null;
      }
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout);
        this.idleTimeout = null;
      }
      if (this.sessionCheckInterval) {
        clearInterval(this.sessionCheckInterval);
        this.sessionCheckInterval = null;
      }
      if (this.pendingRequest) {
        this.pendingRequest.cancel();
        this.pendingRequest = null;
      }
      // Remove event listeners
      if (this.chapterContent) {
        this.chapterContent.removeEventListener('copy', this.preventCopy);
        this.chapterContent.removeEventListener('cut', this.preventCopy);
        this.chapterContent.removeEventListener('contextmenu', this.preventCopy);
      }
      this.activityEvents.forEach(event => {
        try {
          window.removeEventListener(event, this.registerActivity);
        } catch (e) {
          // Optionally log or handle the error
        }
      });
      window.removeEventListener('scroll', this.trackScrollProgression);
      if (this.isMobileDevice) {
        ['touchstart', 'touchend', 'touchmove'].forEach(event => {
          try {
            document.removeEventListener(event, this[`handle${event.charAt(0).toUpperCase() + event.slice(1)}`]);
          } catch (e) {
            // Optionally log or handle the error
          }
        });
      }
      // Clear reading tracking data
      this.readTime = 0;
      this.lastTimeUpdate = 0;
      this.readingActivityScore = 50;
      this.scrollPositions = [];
      
      // Reset reading session for the new chapter
      if (this.isLoggedIn) {
        await this.startReadingSession();
      }
      
      // Fetch new chapter data
      await this.fetchChapter();
      await this.fetchComments();

      // Only start reading timer after chapterViewId is set
      if ((!this.isLoggedIn || this.sessionActive) && this.chapterViewId) {
        this.startReadingTimer();
      } else if (!this.chapterViewId) {
        this.showToast('Gagal mendapatkan ID tampilan bab. Memuat ulang...');
        await this.retryFetchChapterViewId();
      }

      // Re-add listeners and intervals as in mounted()
      const passiveOptions = { passive: true };
      this.activityEvents.forEach(event => {
        window.addEventListener(event, this.registerActivity, passiveOptions);
      });
      window.addEventListener('scroll', this.trackScrollProgression, passiveOptions);
      if (!this.activityCheckInterval) {
        this.activityCheckInterval = setInterval(this.checkActivity, 30000);
      }
      if (this.isMobileDevice) {
        document.addEventListener('touchstart', this.handleTouchStart, passiveOptions);
        document.addEventListener('touchend', this.handleTouchEnd, passiveOptions);
        document.addEventListener('touchmove', this.handleTouchMove, passiveOptions);
      }
      if (this.chapterContent) {
        this.chapterContent.addEventListener('copy', this.preventCopy);
        this.chapterContent.addEventListener('cut', this.preventCopy);
        document.addEventListener('keydown', this.preventKeyShortcuts);
        this.chapterContent.addEventListener('contextmenu', (e) => {
          const userRole = localStorage.getItem("role") || "guest";
          const isPremium = ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all",
            "curator", "finance", "superadmin"].includes(userRole);
          if (!isPremium) {
            e.preventDefault();
            this.showToast('Menu konteks dibatasi pada halaman ini.');
            return false;
          }
        });
      }
    },
  },
  methods: {
    // Device and session management
    generateDeviceId() {
      // Create a reasonably unique device identifier
      const nav = window.navigator;
      const screen = window.screen;
      let guid = nav.mimeTypes ? nav.mimeTypes.length : 0;
      guid += nav.userAgent.replace(/\D+/g, '');
      guid += nav.plugins ? nav.plugins.length : 0;
      guid += screen.height || '';
      guid += screen.width || '';
      guid += screen.pixelDepth || '';
      
      return guid + '-' + Math.random().toString(36).substr(2, 9);
    },
    
    getDeviceInfo() {
      const ua = navigator.userAgent;
      let deviceInfo = 'Unknown';
      
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
        deviceInfo = 'Mobile';
        if (/iPhone|iPad|iPod/.test(ua)) deviceInfo += ' (iOS)';
        if (/Android/.test(ua)) deviceInfo += ' (Android)';
      } else {
        deviceInfo = 'Desktop';
        if (/Windows/.test(ua)) deviceInfo += ' (Windows)';
        if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(ua)) deviceInfo += ' (Mac)';
        if (/Linux/.test(ua)) deviceInfo += ' (Linux)';
      }
      
      return deviceInfo + ' - ' + this.getBrowserInfo();
    },
    
    getBrowserInfo() {
      const ua = navigator.userAgent;
      
      // Browser detection
      if (/Chrome/.test(ua) && !/Edge/.test(ua)) return 'Chrome';
      if (/Firefox/.test(ua)) return 'Firefox';
      if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari';
      if (/MSIE|Trident/.test(ua)) return 'IE';
      if (/Edge/.test(ua)) return 'Edge';
      
      return 'Unknown Browser';
    },
    
    // Update the startReadingSession method in Chapter.vue to use the correct endpoint
  async startReadingSession() {
    if (!this.isLoggedIn) {
      // For non-logged in users, just flag session as active
      this.sessionActive = true;
      return;
    }
    
    try {
      const chapterId = this.chapter.id;
      const token = localStorage.getItem('token');
      
      if (!chapterId || !token) {
        this.sessionActive = false;
        return;
      }
      
      const response = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/api/reading-sessions/start_session/`,
        {
          chapter_id: chapterId,
          device_info: this.getDeviceInfo(),
          session_id: this.sessionId || uuidv4() // Use existing or generate new
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.session_id) {
        this.sessionId = response.data.session_id;
        this.sessionActive = true;
        this.lastSessionValidation = Date.now();
        
        // If session was transferred from another device
        if (response.data.status === 'transferred') {
          this.showSessionAlert = true;
          this.sessionAlertMessage = response.data.message || 'Sesi membaca Anda dialihkan ke perangkat ini.';
        }
      } else {
        this.sessionActive = false;
        this.showToast('Gagal memulai sesi membaca');
      }
      
    } catch (error) {
      console.error('Error starting reading session:', error);
      this.sessionActive = false;
      
      // Handle specific error cases
      if (error.response?.data?.reason === 'other_device') {
        this.showSessionAlert = true;
        this.sessionAlertMessage = error.response.data.message || 'Sesi membaca aktif di perangkat lain.';
        this.otherDeviceInfo = error.response.data.active_device || 'Perangkat lain';
      }
    }
  },

  // Update the validateSession method to use the correct endpoint
  async validateSession() {
    if (!this.isLoggedIn || !this.sessionId) return;
    
    // Don't check too frequently
    const now = Date.now();
    if (now - this.lastSessionValidation < 30000) return; // At least 30s between checks
    
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/api/reading-sessions/validate_session/`,
        {
          chapter_id: this.chapter.id,
          session_id: this.sessionId
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      this.lastSessionValidation = Date.now();
      
      if (response.data.valid) {
        this.sessionActive = true;
      } else {
        this.sessionActive = false;
        
        // Handle different invalid session reasons
        if (response.data.reason === 'other_device') {
          this.showSessionAlert = true;
          this.sessionAlertMessage = response.data.message || 'Sesi membaca aktif di perangkat lain.';
          this.otherDeviceInfo = response.data.active_device || 'Perangkat lain';
          
          // Stop the timer
          this.stopReadingTimer();
        } else if (response.data.reason === 'session_expired') {
          // Session expired, try to restart
          await this.startReadingSession();
        }
      }
    } catch (error) {
      console.error('Error validating reading session:', error);
      // On network error, assume session is still valid to avoid disrupting the user
    }
  },
      
    continueWithCurrentDevice() {
      // Force transfer session to this device
      this.startReadingSession();
      this.closeSessionAlert();
    },
    
    closeSessionAlert() {
      this.showSessionAlert = false;
      this.sessionAlertMessage = '';
      this.otherDeviceInfo = '';
      
      // If we don't have an active session, go back to book page
      if (!this.sessionActive && this.isLoggedIn) {
        this.$router.push(`/book/${this.bookSlug}`);
      }
    },
    
    // Activity tracking
    registerActivity() {
      this.lastActivityTime = Date.now();
      this.isUserActive = true;
      
      // Re-start reading timer if it was stopped due to inactivity
      if (!this.readingInterval && this.isUserActive) {
        this.startReadingTimer();
      }
    },
    
    checkActivity() {
      const now = Date.now();
      if (now - this.lastActivityTime > this.maxInactivityTime) {
        // User is idle
        this.isUserActive = false;
        
        // Stop tracking read time temporarily
        if (this.readingInterval) {
          clearInterval(this.readingInterval);
          this.readingInterval = null;
        }
      }
    },
    
    trackScrollProgression() {
      // Save current scroll position
      const currentPosition = window.scrollY;
      const documentHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercentage = (currentPosition / documentHeight) * 100;
      
      // Add to scroll history with timestamp
      this.scrollPositions.push({
        position: scrollPercentage,
        time: Date.now()
      });
      
      // Limit history to 20 positions
      if (this.scrollPositions.length > 20) {
        this.scrollPositions.shift();
      }
      
      // Analyze reading pattern
      this.analyzeReadingPattern();
      
      // Update last scroll position
      this.lastScrollPosition = currentPosition;
      
      // This is considered user activity
      this.registerActivity();
    },
    
    analyzeReadingPattern() {
      if (this.scrollPositions.length < 3) return;
      
      // Calculate average scroll speed
      let totalSpeed = 0;
      for (let i = 1; i < this.scrollPositions.length; i++) {
        const timeDiff = this.scrollPositions[i].time - this.scrollPositions[i-1].time;
        const positionDiff = Math.abs(this.scrollPositions[i].position - this.scrollPositions[i-1].position);
        
        if (timeDiff > 0) {
          const speed = positionDiff / timeDiff;
          totalSpeed += speed;
        }
      }
      
      const avgSpeed = totalSpeed / (this.scrollPositions.length - 1);
      
      // Check if scrolling pattern looks like reading
      const isProgressive = this.scrollPositions[this.scrollPositions.length - 1].position > 
                           this.scrollPositions[0].position;
      
      // Weight reading activity score
      if (isProgressive && avgSpeed < 0.01) {
        // Slow downward scroll: likely reading
        this.readingActivityScore = Math.min(100, this.readingActivityScore + 5);
      } else if (avgSpeed > 0.05) {
        // Fast scroll: likely skimming
        this.readingActivityScore = Math.max(10, this.readingActivityScore - 5);
      }
    },
    
    // Touch handling for mobile
    handleTouchStart(event) {
      this.touchStartTimestamp = Date.now();
      if (event.touches.length > 0) {
        this.lastTouchPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
      }
      this.registerActivity();
    },
    
    handleTouchEnd() {
      this.touchEndTimestamp = Date.now();
      
      // Analyze touch duration
      const touchDuration = this.touchEndTimestamp - this.touchStartTimestamp;
      
      // Long touches usually indicate user is reading/selecting text
      if (touchDuration > 500) {
        this.readingActivityScore = Math.min(100, this.readingActivityScore + 5);
      }
      
      this.registerActivity();
    },
    
    handleTouchMove(event) {
      if (event.touches.length > 0) {
        const currentPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
        
        // Calculate delta movement
        const deltaY = currentPosition.y - this.lastTouchPosition.y;
        
        // Record movement with timestamp
        this.touchMovements.push({
          deltaY: deltaY,
          timestamp: Date.now()
        });
        
        // Limit movement history
        if (this.touchMovements.length > 20) {
          this.touchMovements.shift();
        }
        
        // Analyze touch pattern
        this.analyzeTouchPattern();
        
        // Update last position
        this.lastTouchPosition = currentPosition;
      }
      
      this.registerActivity();
    },
    
    analyzeTouchPattern() {
      if (this.touchMovements.length < 3) return;
      
      // Check movement speed
      let fastMovements = 0;
      let slowMovements = 0;
      
      for (let i = 1; i < this.touchMovements.length; i++) {
        const timeDiff = this.touchMovements[i].timestamp - this.touchMovements[i-1].timestamp;
        const distance = Math.abs(this.touchMovements[i].deltaY);
        
        if (timeDiff > 0) {
          const speed = distance / timeDiff;
          
          if (speed > 0.5) { // Fast movement (flick)
            fastMovements++;
          } else if (speed < 0.1 && distance > 0) { // Slow movement
            slowMovements++;
          }
        }
      }
      
      // Update activity score based on pattern
      if (slowMovements > fastMovements * 2) {
        // Pattern of slow scrolling indicates reading
        this.readingActivityScore = Math.min(100, this.readingActivityScore + 10);
      } else if (fastMovements > slowMovements * 2) {
        // Many flick scrolls indicate skimming
        this.readingActivityScore = Math.max(10, this.readingActivityScore - 5);
      }
    },
    
    trackVisibilityChange() {
      if (document.hidden) {
        this.pageSwitches++;
      }
    },
    
    // Reading time tracking
    startReadingTimer() {
      /*if (!this.chapterViewId) {
        this.showToast('Tidak dapat memulai timer: ID tampilan bab tidak tersedia.');
        return;
      }*/
      this.readingStartTime = Date.now();
      this.lastTimeUpdate = this.readTime;
      
      // Set interval to update read time counter
      this.readingInterval = setInterval(() => {
        if (!this.isIdle && this.isVisible && this.isUserActive) {
          this.readTime += 1;
          
          // Send update to server every 30 seconds
          if (this.readTime - this.lastTimeUpdate >= 30) {
            this.sendReadingTime();
          }
        }
      }, 1000);
      
      // Set idle timer
      this.resetIdleTimer();
    },
    
    stopReadingTimer() {
      if (this.readingInterval) {
        clearInterval(this.readingInterval);
        this.readingInterval = null;
      }
    },
    
    resetReadingTimer() {
      this.stopReadingTimer();
      this.startReadingTimer();
    },
    
    resetIdleTimer() {
      // Clear existing idle timeout
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout);
      }
      
      // Set user as active
      if (this.isIdle) {
        this.isIdle = false;
      }
      
      // Set new idle timeout
      this.idleTimeout = setTimeout(() => {
        this.isIdle = true;
        console.log('User is idle - pausing reading timer');
      }, this.maxIdleTime);
    },
    
    calculateEffectiveReadTime() {
      // If user is idle or has low activity score, reduce time
      let effectiveTime = this.readTime - this.lastTimeUpdate;
      
      // Adjust based on reading activity score
      if (this.readingActivityScore < 30) {
        // Low activity, cut reading time by up to 80%
        effectiveTime = Math.floor(effectiveTime * 0.2);
      } else if (this.readingActivityScore < 60) {
        // Medium activity, cut reading time by up to 40%
        effectiveTime = Math.floor(effectiveTime * 0.6);
      }
      
      // Limit maximum time based on estimated_read_time with device adjustment
      let maxAllowedTime = this.chapter.estimated_read_time * 60;
      
      // Mobile users tend to read slower
      if (this.isMobileDevice) {
        maxAllowedTime *= 1.75; // 75% more time for mobile
      } else {
        maxAllowedTime *= 1.5; // 50% more time for desktop
      }
      
      // Reduce max time if there are many page switches
      if (this.pageSwitches > 10) {
        maxAllowedTime *= 0.7;
      } else if (this.pageSwitches > 5) {
        maxAllowedTime *= 0.9;
      }
      
      return Math.min(effectiveTime, maxAllowedTime);
    },
    
    async sendReadingTime() {
      if (this.isDestroyed || !this.chapterViewId || this.readTime <= this.lastTimeUpdate) return;
      
      // For logged-in users, only send if session is active
      if (this.isLoggedIn && !this.sessionActive) {
        console.log('Not sending reading time: inactive session');
        return;
      }
      
      // Calculate effective time to send based on activity metrics
      const effectiveTime = this.calculateEffectiveReadTime();
      
      // Only send if we have meaningful time to report
      if (effectiveTime <= 0) return;
      
      try {
        if (this.pendingRequest) {
          this.pendingRequest.cancel();
        }
        
        const CancelToken = axios.CancelToken;
        this.pendingRequest = CancelToken.source();
        const payload = {
          view_id: this.chapterViewId,
          duration: effectiveTime,
          activity_score: this.readingActivityScore,
          is_mobile: this.isMobileDevice
        };
        
        // Add session ID for logged-in users
        if (this.isLoggedIn && this.sessionId) {
          payload.session_id = this.sessionId;
        }
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/chapters/update-read-duration/`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            cancelToken: this.pendingRequest.token
          }
        );
        
        if (response.data.success) {
          this.lastTimeUpdate = this.readTime;
          console.log(`Successfully updated reading time: +${effectiveTime}s`);
        }
        
        // Check for session invalidation
        if (response.data.code === 'invalid_session') {
          this.sessionActive = false;
          this.showToast('Sesi membaca Anda tidak valid. Muat ulang halaman untuk memulai kembali.');
          this.stopReadingTimer();
        }
        
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request canceled:', error.message);
        } else {
          console.error('Failed to update reading time:', error);
          // If session invalid, stop tracking
          if (error.response?.data?.code === 'invalid_session') {
            this.sessionActive = false;
            this.stopReadingTimer();
            
            // Try to restart session
            this.startReadingSession();
          }
        }
        
      } finally {
        this.pendingRequest = null;
      }
    },
    
    handleVisibilityChange() {
      if (document.hidden) {
        // Tab not active
        this.isVisible = false;
        // Send accumulated reading time
        this.sendReadingTime();
      } else {
        // Tab active again
        this.isVisible = true;
        // Reset idle timer
        this.resetIdleTimer();
        
        // Validate session for logged-in users
        if (this.isLoggedIn && this.sessionId) {
          this.validateSession();
        }
      }
    },
    
    handleBeforeUnload() {
      // Send reading data before user leaves
      this.sendReadingTime();
    },
    
    // UI functions
    formatDuration(seconds) {
      if (seconds < 60) {
        return `${seconds} detik`;
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} menit`;
      } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (minutes > 0) {
          return `${hours} jam ${minutes} menit`;
        }
        return `${hours} jam`;
      }
    },
    
    handleResize() {
      this.isDesktop = window.innerWidth > 900;
    },
    
    // Chapter data and UI functions
    async fetchChapter() {
      const chapterSlug = this.$route.params.chapterSlug;
      this.chapter = { ...this.chapter, title: "Loading...", content: "Loading..." };

      try {
        // Fetch current chapter
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/chapters/${chapterSlug}/`);
        this.chapter = response.data;
        
        // Save ID for chapter view
        if (response.data.chapter_view_id) {
          this.chapterViewId = response.data.chapter_view_id;
        }
        
        this.checkChapterAccess();

        // Fetch all chapters for navigation
        const bookResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/books/${this.bookSlug}/`);
        this.chapters = bookResponse.data.chapters || [];

        // Assign book details for sidebar
        this.chapter.book_cover = bookResponse.data.cover || this.defaultCover;
        this.chapter.book_title = bookResponse.data.title || "Unknown Book";
        
        // Reset timer and data
        this.readingActivityScore = 50;
        this.pageSwitches = 0;
        this.resetReadingTimer();

      } catch (err) {
        console.error("Failed to fetch chapter details", err);
      }
    },
    
    async fetchComments() {
      const chapterSlug = this.$route.params.chapterSlug;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/chapters/${chapterSlug}/comments/`
        );
        this.comments = response.data;
      } catch (err) {
        console.error("Failed to fetch comments", err);
        if (err.response && err.response.status === 401) {
          Swal.fire({
            icon: 'info',
            text: 'Anda perlu login untuk melihat komentar.',
          });
        } else {
          Swal.fire({
            icon: 'info',
            text: 'Gagal memuat komentar. Silakan coba lagi',
          });
        }
      }
    },
    
    async submitComment() {
      if (!this.newComment.trim()) {
        Swal.fire({
          icon: 'info',
          text: 'Komentar tidak boleh kosong.',
        });
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const chapterSlug = this.$route.params.chapterSlug;

        const strippedComment = this.stripHtmlTags(this.newComment);

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/chapters/${chapterSlug}/create-comment/`,
          { content: strippedComment },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 201) {
          this.comments.push(response.data);
          this.newComment = "";
        }
      } catch (err) {
        console.error("Failed to submit comment", err);
        Swal.fire({
          icon: 'info',
          text: 'Gagal mengirim komentar. Silakan dicoba lagi.',
        });
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return "Unknown";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },
    
    formatDateTime(dateString) {
      if (!dateString) return "Unknown";
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    
    showToast(message) {
      const now = Date.now();
      const TOAST_COOLDOWN = 2000;
      if (now - this.lastToastTime < TOAST_COOLDOWN) return;
      
      this.lastToastTime = now;
      Swal.fire({
        icon: 'info',
        title: 'Perhatian',
        text: message,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    },
    
    showLoginModal() {
      this.isLoginModalVisible = true;
    },
    
    handleLoginSuccess() {
      this.isLoginModalVisible = false;
      this.userRole = localStorage.getItem("role") || "guest";
      this.isLoggedIn = true;
      // Reload the page to reflect changes
      window.location.reload();
    },
    
    redirectToRegister() {
      this.$router.push("/register");
    },
    
    checkChapterAccess() {
      if (!this.chapter.order_number) return;

      if (this.canAccessChapter(this.chapter)) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
        this.chapter.title = '';
        this.chapter.content = '';
        this.$router.push("/login");
      }
    },
    
    canAccessChapter(ch) {
      if (ch.order_number === 1) return true;
      if (ch.order_number === 2 && this.isLoggedIn) return true;
      if (this.isSubscriber() || this.isAdmin()) return true;
      return false;
    },
    
    isSubscriber() {
      return ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all"].includes(this.userRole);
    },
    
    isAdmin() {
      return ["curator", "finance", "superadmin"].includes(this.userRole);
    },
  },
};
</script>

<style scoped>
/*  Chapter container styling */
.chapter-container {
  flex: 1;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, .1);
}
.chapter-page {
  display: flex;
  max-width: 1200px;
  margin: 40px auto;
}

.chapter-sidebar {
  width: 250px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}

.sidebar-cover {
  width: 100%;
  border-radius: 5px;
  margin-bottom: 10px;
}

.sidebar-title {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.chapter-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.chapter-list li {
  padding: 10px;
  transition: background 0.3s ease-in-out;
  border-radius: 5px;
}

.chapter-list li a {
  display: block;
  text-decoration: none;
  color: #488e71;
  padding: 10px;
  border-radius: 5px;
}

.chapter-list li.active-chapter {
  background: white;
  border-left: 5px solid #f66a24;
}

.restricted-chapter {
  color: gray;
  cursor: pointer;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}

.comment-section {
  margin-top: 30px;
}

.comment-form textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ddd;
  margin-bottom: 10px;
}

.comment-item {
  border-bottom: 1px solid #eee;
  padding: 15px 0;
}

.comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.comment-user {
  font-weight: bold;
  margin-right: 10px;
}

.comment-date {
  color: #777;
  font-size: 0.9em;
}

.comment-content {
  color: #333;
  line-height: 1.5;
}

.no-comments {
  font-style: italic;
  color: #777;
  text-align: center;
  padding: 20px;
}

.back-link {
  display: inline-block;
  margin-bottom: 15px;
  color: #488e71;
  text-decoration: none;
  transition: color 0.3s;
}

.back-link:hover {
  color: #f66a24;
}

.login-link {
  color: #488e71;
  cursor: pointer;
  text-decoration: underline;
}

.login-link:hover {
  color: #f66a24;
}

.book-cover-container {
  position: relative;
  width: 100%;
  margin-bottom: 15px;
}

.sidebar-cover {
  width: 100%;
  height: auto;
  border-radius: 5px;
  margin-bottom: 0;
}

.book-title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 8px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

.book-title-overlay h3 {
  color: white;
  font-size: 1.1rem;
  margin-bottom: 15px;
  text-align: center;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Remove the original standalone sidebar title */
.sidebar-title {
  display: none;
}

/* Improved copy protection with better UX */
.chapter-content {
  user-select: text; /* Allow text selection for better reading experience */
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  position: relative;
}

.chapter-content img {
  user-select: none; /* Prevent image selection */
  -webkit-user-drag: none; /* Prevent image dragging */
  pointer-events: none; /* Prevent image interaction */
}

.copy-protection-notice {
  background-color: #fff8e1;
  color: #856404;
  padding: 8px 12px;
  border-radius: 4px;
  margin: 15px 0;
  font-size: 14px;
  border-left: 4px solid #ffc107;
  text-align: center;
}

/* Premium user style - add a subtle indicator for premium content */
.premium-content {
  position: relative;
}

.premium-content::after {
  content: "Premium";
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(72, 142, 113, 0.7);
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  pointer-events: none;
}

.default-avatar {
  margin-right: 5px;
}

/* Mobile styles */
@media (max-width: 900px) {
  .chapter-page {
    flex-direction: column;
    padding: 0 15px;
  }

  .mobile-nav {
    margin-bottom: 20px;
  }

  .menu-btn {
    width: 100%;
    padding: 10px;
    background: #f8f9fa;
    border: none;
    border-radius: 5px;
    text-align: left;
    font-weight: bold;
    cursor: pointer;
  }

  .dropdown-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: white;
    border-radius: 5px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .dropdown-list li {
    padding: 10px;
    border-bottom: 1px solid #eee;
  }

  .dropdown-list li:last-child {
    border-bottom: none;
  }
}
</style>
